import * as React from 'react'
import { Link } from '@tanstack/react-router'
import { getErrorDescription } from '@/lib/error-utils'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'
import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { dateToDateOnlyTimestamp, timestampToDateOnly } from '@/lib/date-utils'
import {
  Calendar,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Plus,
  Pencil,
  MoreVertical,
  Loader2,
  Timer,
  RefreshCw,
  Download,
} from 'lucide-react'
import { format, isPast, isToday, isTomorrow } from 'date-fns'

import type { PaymentSchedule } from '@/gen/cortado/v1/invoices_pb'
import { UpdatePaymentScheduleRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { PaymentScheduleStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { formatCurrency } from '@/lib/currency-utils'
import { cn } from '@/lib/utils'
import { useUpdatePaymentSchedule } from '@/stores/payments.store'
import { useGeneratePaymentReceiptPdf } from '@/stores/invoices.store'
import { generateAndDownloadFile } from '@/lib/download-file'

import { ActionGate } from '@/components/shared/action-gate'
import { AnimatedCollapse, CollapseChevron } from '@/components/shared/animated-collapse'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/shared/date-picker'

interface PaymentSchedulesCardProps {
  invoiceId: string
  /** The invoice's own organization — what the permissions are checked against. */
  organizationId: string
  paymentSchedules: PaymentSchedule[]
  isLocked: boolean
  isDraft?: boolean
  onRecordPayment: (schedule: PaymentSchedule) => void
}

/** Editing schedule structure (description/due date) — invoice authoring. */
const INVOICES_MANAGE = 'InvoicesManage'
/** Recording a payment against a schedule — moving money. */
const PAYMENTS_MANAGE = 'PaymentsManage'

const scheduleStatusConfig: Record<
  number,
  {
    label: string
    badge: 'default' | 'success' | 'warning' | 'danger' | 'secondary' | 'info'
    icon: typeof CheckCircle2
    color: string
  }
> = {
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PENDING]: {
    label: 'Pending',
    badge: 'secondary',
    icon: Clock,
    color: 'text-muted-foreground',
  },
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAYMENT_INITIATED]: {
    label: 'Processing',
    badge: 'info',
    icon: Loader2,
    color: 'text-status-info-fg',
  },
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PARTIAL]: {
    label: 'Partial',
    badge: 'warning',
    icon: Clock,
    color: 'text-status-warning-fg',
  },
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAID]: {
    label: 'Paid',
    badge: 'success',
    icon: CheckCircle2,
    color: 'text-status-success-fg',
  },
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_OVERDUE]: {
    label: 'Overdue',
    badge: 'danger',
    icon: AlertTriangle,
    color: 'text-status-danger-fg',
  },
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_CANCELLED]: {
    label: 'Cancelled',
    badge: 'secondary',
    icon: Clock,
    color: 'text-muted-foreground',
  },
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_WAIVED]: {
    label: 'Waived',
    badge: 'secondary',
    icon: CheckCircle2,
    color: 'text-muted-foreground',
  },
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_REFUNDED]: {
    label: 'Refunded',
    badge: 'secondary',
    icon: RefreshCw,
    color: 'text-muted-foreground',
  },
  [PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_DISPUTED]: {
    label: 'Disputed',
    badge: 'danger',
    icon: AlertTriangle,
    color: 'text-status-danger-fg',
  },
}

function getScheduleStatusConfig(status: PaymentScheduleStatusEnum) {
  return (
    scheduleStatusConfig[status] || {
      label: 'Unknown',
      badge: 'secondary' as const,
      icon: Clock,
      color: 'text-muted-foreground',
    }
  )
}

function formatDueDate(timestamp: { seconds: bigint } | undefined): string {
  const date = timestampToDateOnly(timestamp)
  if (!date) return 'No due date'

  if (isToday(date)) return 'Due today'
  if (isTomorrow(date)) return 'Due tomorrow'
  if (isPast(date)) return `Overdue (${format(date, 'MMM d')})`

  return `Due ${format(date, 'MMM d, yyyy')}`
}

function getActiveCheckoutExpiresAt(schedule: PaymentSchedule): Date | null {
  return schedule.activeCheckoutSession?.expiresAt
    ? timestampDate(schedule.activeCheckoutSession.expiresAt)
    : null
}

function isScheduleCheckoutSessionActive(schedule: PaymentSchedule, nowMs = Date.now()): boolean {
  const expiresAt = getActiveCheckoutExpiresAt(schedule)
  return Boolean(expiresAt && expiresAt.getTime() > nowMs)
}

export function hasCompletedPaymentForReceipt(schedule: PaymentSchedule): boolean {
  return schedule.amountPaidCents > 0n ||
    schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAID ||
    schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PARTIAL ||
    schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_REFUNDED ||
    schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_DISPUTED
}

function ActiveCheckoutCountdown({ expiresAt, nowMs }: { expiresAt: Date; nowMs: number }) {
  const diff = expiresAt.getTime() - nowMs
  const isExpired = diff <= 0
  const minutes = Math.floor(Math.max(diff, 0) / 60000)
  const seconds = Math.floor((Math.max(diff, 0) % 60000) / 1000)
  const label = isExpired ? 'expired' : `${minutes}:${seconds.toString().padStart(2, '0')}`

  return (
    <span className={cn(
      'font-financial inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs',
      isExpired ? 'bg-status-danger-bg text-status-danger-fg' : 'bg-status-warning-bg text-status-warning-fg'
    )}>
      <Timer className="h-3 w-3" />
      {label}
    </span>
  )
}

export function PaymentSchedulesCard({
  invoiceId,
  organizationId,
  paymentSchedules,
  isLocked,
  isDraft = false,
  onRecordPayment,
}: PaymentSchedulesCardProps) {
  const [isExpanded, setIsExpanded] = React.useState(true)

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = React.useState(false)
  const [selectedSchedule, setSelectedSchedule] = React.useState<PaymentSchedule | null>(null)

  // Edit form state
  const [editDescription, setEditDescription] = React.useState('')
  const [editDueDate, setEditDueDate] = React.useState<Date | undefined>()
  const [editAmount, setEditAmount] = React.useState('')

  const [downloadingReceiptId, setDownloadingReceiptId] = React.useState<string | null>(null)

  const { updateSchedule, isUpdating } = useUpdatePaymentSchedule(invoiceId)
  const { generateReceiptPdf } = useGeneratePaymentReceiptPdf()

  // Sort schedules by sequence number
  const sortedSchedules = React.useMemo(
    () => [...paymentSchedules].sort((a, b) => a.sequenceNumber - b.sequenceNumber),
    [paymentSchedules]
  )
  const hasCheckoutSessionSnapshots = sortedSchedules.some((schedule) => Boolean(schedule.activeCheckoutSession))
  const [nowMs, setNowMs] = React.useState(() => Date.now())

  React.useEffect(() => {
    if (!hasCheckoutSessionSnapshots) return

    const initialNowMs = Date.now()
    setNowMs(initialNowMs)
    if (!sortedSchedules.some((schedule) => isScheduleCheckoutSessionActive(schedule, initialNowMs))) return

    const interval = window.setInterval(() => {
      const nextNowMs = Date.now()
      setNowMs(nextNowMs)
      if (!sortedSchedules.some((schedule) => isScheduleCheckoutSessionActive(schedule, nextNowMs))) {
        window.clearInterval(interval)
      }
    }, 1000)
    return () => window.clearInterval(interval)
  }, [hasCheckoutSessionSnapshots, sortedSchedules])

  const hasSchedules = sortedSchedules.length > 0

  // Can only edit/delete if invoice is not locked (no payments have been made).
  // The permission half is handled by ActionGate on the menu item itself.
  const canModify = !isLocked

  const handleEditClick = (schedule: PaymentSchedule) => {
    if (isScheduleCheckoutSessionActive(schedule)) {
      toast.error('Payment schedule is locked', {
        description: 'A customer has an active checkout link for this payment.',
      })
      return
    }
    setSelectedSchedule(schedule)
    setEditDescription(schedule.description || '')
    setEditDueDate(schedule.dueDate ? timestampToDateOnly(schedule.dueDate) : undefined)
    setEditAmount(formatCurrency(Number(schedule.amountCents || 0n)).replace('$', ''))
    setEditDialogOpen(true)
  }

  const handleDownloadReceipt = async (schedule: PaymentSchedule) => {
    setDownloadingReceiptId(schedule.id)
    try {
      await generateAndDownloadFile(
        () => generateReceiptPdf(invoiceId, schedule.id),
        `Receipt-INV-Payment-${schedule.sequenceNumber}.pdf`
      )
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), {
        context: 'payment-schedules-card-download-receipt',
      })
      toast.error('Failed to download payment receipt', {
        description: getErrorDescription(error),
      })
    } finally {
      setDownloadingReceiptId(null)
    }
  }

  const handleUpdateSchedule = async () => {
    if (!selectedSchedule) return

    try {
      const paths: string[] = []

      if (editDescription !== (selectedSchedule.description || '')) {
        paths.push('description')
      }
      if (editDueDate) {
        paths.push('due_date')
      }

      if (paths.length === 0) {
        setEditDialogOpen(false)
        return
      }

      await updateSchedule(
        create(UpdatePaymentScheduleRequestSchema, {
          id: selectedSchedule.id,
          description: editDescription || undefined,
          dueDate: editDueDate ? dateToDateOnlyTimestamp(editDueDate) : undefined,
          updateMask: create(FieldMaskSchema, { paths }),
        })
      )
      setEditDialogOpen(false)
      setSelectedSchedule(null)
      toast.success('Payment schedule updated')
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'payment-schedules-update' })
      toast.error('Failed to update schedule', {
        description: getErrorDescription(error),
      })
    }
  }

  return (
    <>
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">
              Payment Schedule
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({sortedSchedules.length} {sortedSchedules.length === 1 ? 'payment' : 'payments'})
              </span>
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Hide payment schedule' : 'Show payment schedule'}
            onClick={() => setIsExpanded((open) => !open)}
          >
            <CollapseChevron open={isExpanded} className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <AnimatedCollapse open={isExpanded}>
        <CardContent className="pt-0">
          {!hasSchedules ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No payment schedule defined</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedSchedules.map((schedule, index) => {
                const statusConfig = getScheduleStatusConfig(schedule.status)

                const amountCents = Number(schedule.amountCents || 0n)
                const paidCents = Number(schedule.amountPaidCents || 0n)
                const goodwillRefundedCents = Number(schedule.goodwillRefundedCents || 0n)
                const hasGoodwillRefund = goodwillRefundedCents > 0
                // Effective amount due accounts for goodwill refund
                const effectiveAmountDue = amountCents - goodwillRefundedCents
                const remainingCents = Math.max(0, effectiveAmountDue - paidCents)
                const progressPercentage =
                  effectiveAmountDue > 0 ? Math.min((paidCents / effectiveAmountDue) * 100, 100) : 0
                // Refunded / disputed are terminal reversal states. Don't let the
                // amount-covered clause mark them green "Paid", and don't offer to log a
                // new payment against them — the explicit status badge tells the story.
                const isReversed =
                  schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_REFUNDED ||
                  schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_DISPUTED
                // Schedule is effectively paid if amount paid covers effective amount due
                const isPaid = !isReversed &&
                  (schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAID ||
                  (paidCents >= effectiveAmountDue && effectiveAmountDue > 0))
                const isOverdue = schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_OVERDUE
                // A payment already submitted via checkout (e.g. ACH, settling over
                // several business days) must not offer "Log Payment" — that would
                // invite staff to double-record a payment already in flight.
                const isProcessing = schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAYMENT_INITIATED
                const canPay = remainingCents > 0 && !isPaid && !isReversed && !isProcessing
                const activeCheckoutExpiresAt = getActiveCheckoutExpiresAt(schedule)
                const hasActiveCheckoutSession = isScheduleCheckoutSessionActive(schedule, nowMs)
                const hasReceipt = hasCompletedPaymentForReceipt(schedule)

                return (
                  <div
                    key={schedule.id}
                    className={cn(
                      'rounded-lg border p-4 transition-all',
                      isPaid && 'bg-status-success-bg border-status-success-border',
                      isOverdue && 'bg-status-danger-bg border-status-danger-border',
                      !isPaid && !isOverdue && 'bg-muted/30'
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Schedule info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-muted-foreground">
                            Payment {index + 1}
                          </span>
                          {schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_DISPUTED ? (
                            <Link
                              to="/payments"
                              search={{ section: 'disputes' }}
                              aria-label="View disputes"
                            >
                              <Badge
                                variant={statusConfig.badge}
                                className="cursor-pointer hover:underline"
>
                                {statusConfig.label}
                              </Badge>
                            </Link>
                          ) : (
                            <Badge variant={statusConfig.badge}>
                              {statusConfig.label}
                            </Badge>
                          )}
                          {hasActiveCheckoutSession && (
                            <Badge variant="warning">
                              Payment link active
                            </Badge>
                          )}
                          {activeCheckoutExpiresAt && (
                            <ActiveCheckoutCountdown expiresAt={activeCheckoutExpiresAt} nowMs={nowMs} />
                          )}
                        </div>

                        <p className="font-medium text-sm truncate">
                          {schedule.description || `Installment ${index + 1}`}
                        </p>

                        <p className={cn(
                          'text-xs mt-1',
                          isOverdue ? 'text-status-danger-fg font-medium' : 'text-muted-foreground'
                        )}>
                          {formatDueDate(schedule.dueDate)}
                        </p>

                        {/* Goodwill refund indicator */}
                        {hasGoodwillRefund && (
                          <p className="font-financial text-xs text-status-success-fg mt-1">
                            -{formatCurrency(goodwillRefundedCents)} goodwill refund
                          </p>
                        )}

                        {/* Progress bar for partial payments */}
                        {progressPercentage > 0 && progressPercentage < 100 && (
                          <div className="mt-2">
                            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                              <div
                                className="h-full rounded-full bg-status-success-fg"
                                style={{ width: `${progressPercentage}%` }}
                              />
                            </div>
                            <p className="font-financial text-[10px] text-muted-foreground mt-1">
                              {formatCurrency(paidCents)} of {formatCurrency(effectiveAmountDue)} paid
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Right: Amount and action */}
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-start gap-1">
                          <div className="text-right">
                            <p className={cn(
                              'font-financial text-lg font-bold',
                              isPaid && 'text-status-success-fg',
                              isOverdue && 'text-status-danger-fg'
                            )}>
                              {formatCurrency(amountCents)}
                            </p>
                            {canPay && paidCents > 0 && (
                              <p className="font-financial text-xs text-muted-foreground">
                                {formatCurrency(remainingCents)} remaining
                              </p>
                            )}
                          </div>

                          {/* Edit/Delete Menu - only when invoice is not locked */}
                          {canModify && !hasActiveCheckoutSession && schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PENDING && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2">
                                  <MoreVertical className="h-4 w-4" />
                                  <span className="sr-only">Payment schedule actions</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <ActionGate
                                  organizationId={organizationId}
                                  permission={INVOICES_MANAGE}
                                  verb="edit"
                                  noun="payment schedules"
                                >
                                  <DropdownMenuItem onClick={() => handleEditClick(schedule)}>
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Edit
                                  </DropdownMenuItem>
                                </ActionGate>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>

                        {!isDraft && canPay && !hasActiveCheckoutSession && (
                          <ActionGate
                            organizationId={organizationId}
                            permission={PAYMENTS_MANAGE}
                            verb="record"
                            noun="payments"
                          >
                            <Button
                              size="sm"
                              variant={isOverdue ? 'destructive' : 'default'}
                              onClick={() => onRecordPayment(schedule)}
                              className="gap-1"
                            >
                              <Plus className="h-3 w-3" />
                              Log Payment
                            </Button>
                          </ActionGate>
                        )}

                        {isPaid && (
                          <div className="flex items-center gap-1 text-status-success-fg">
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-xs font-medium">Paid</span>
                          </div>
                        )}

                        {hasReceipt && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            aria-label={`Download receipt for payment ${schedule.sequenceNumber}`}
                            disabled={downloadingReceiptId === schedule.id}
                            onClick={() => handleDownloadReceipt(schedule)}
                            className="gap-1"
                          >
                            {downloadingReceiptId === schedule.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Download className="h-3.5 w-3.5" />
                            )}
                            Receipt
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </AnimatedCollapse>
    </Card>

    {/* Edit Payment Schedule Dialog */}
    <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Payment Schedule</DialogTitle>
          <DialogDescription>
            Modify the payment schedule details. Changes can only be made before any payments are recorded.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Input
              id="edit-description"
              placeholder="e.g., Deposit, Final Payment"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-due-date">Due Date</Label>
            <DatePicker
              value={editDueDate}
              onChange={setEditDueDate}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-amount">Amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="edit-amount"
                type="text"
                className="pl-7"
                placeholder="0.00"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                disabled
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Amounts can only be changed from the invoice editor.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleUpdateSchedule} disabled={isUpdating}>
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
